import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { min, subtract, add, multiply, cross, norm, max, divide, e } from 'mathjs';
import {
  faBackward,
  faFastBackward,
  faFastForward,
  faForward,
  faPause,
  faPlay
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { act } from 'react-dom/test-utils';
import { singleProblemParametrizationState } from '../../atoms/singleProblemParametrizationState';
import {
  arrowHeadRelativePosition,
  edgeSpacing,
  fontSize,
  initalZoom,
  labelOffset,
  lineWidth,
  nodeSize,
  zoomScroll
} from '../../config';
import { generateProblemWithLocation } from '../../core/problem-generator';
import { MaxFlowProblem } from '../../typings';
import SingleProblemParametrization from '../SingleProblemParametrization/SingleProblemParametrization';
import styles from './Visualization.module.scss';
import { normalize } from '../../core/helpers';
import { stepFordFulkerson } from '../../core/FordFulkersonSolver';
import { stepEdmondsKarp } from '../../core/EdmondsKarpSolver';
import { stepDinic } from '../../core/DinicSolver';
import { stepGoldbergTarjan } from '../../core/GoldbergTarjanSolver';

const playSpeeds = [Infinity, 3000, 1000, 400, 0];

interface VisualizationProps {
  algo: 'ford-fulkerson' | 'edmonds-karp' | 'dinic' | 'goldberg-tarjan';
}

const Visualization: FC<VisualizationProps> = ({ algo }) => {
  const [showParametrization, setShowParametrization] = useState(true);
  const [problems, setProblems] = useState<MaxFlowProblem[]>([]);
  const [activeProblem, setActiveProblem] = useState<number>(0);
  const [finisheds, setFinisheds] = useState<boolean[]>([false]);
  const [augmentedPaths, setAugmentedPaths] = useState<number[][]>([[]]);
  const [levelEdges, setLevelEdges] = useState<boolean[][][]>([[]]);
  const [blockingEdges, setBlockingEdges] = useState<boolean[][][]>([[]]);
  const [distanceLabels, setDistanceLabels] = useState<number[][]>([[]]);
  const q = useRef<number[]>([]);
  const curEdge = useRef<number>(0);
  const xf = useRef<number[]>([]);
  const goldBergTarjanInitDone = useRef<boolean>(false);
  const qHead = useRef<number>(0);
  const qTail = useRef<number>(0);
  const [zoom, setZoom] = useState(initalZoom);
  const [dragOffset, setDragOffset] = useState([0, 0]);
  const singleProblemParametrization = useRecoilValue(singleProblemParametrizationState);
  const [canvas, setCanvas] = useState<HTMLCanvasElement>();
  const canvasRef = useCallback((node) => {
    setCanvas(node);
  }, []);
  const dragging = useRef(false);
  const smallDim = useRef(1);
  const [playSpeed, setPlaySpeed] = useState<0 | 1 | 2 | 3 | 4>(0);
  const tLastTick = useRef<DOMHighResTimeStamp>(performance.now());
  const animationFrame = useRef<number>(0);
  const stepForwardLocked = useRef<boolean>(false);

  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    setZoom((old) => max(initalZoom, zoomScroll(old, e.deltaY)));
  };

  const dragStart = () => {
    dragging.current = true;
  };

  const drag = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    setDragOffset(
      (old) => add(old, divide([e.movementX, e.movementY], smallDim.current * zoom)) as number[]
    );
  };

  const dragEnd = () => {
    dragging.current = false;
  };

  const stepBack = () => {
    setActiveProblem((old) => (old > 0 ? old - 1 : 0));
  };

  const stepForward = () => {
    if (problems.length === 0) return;
    if (finisheds[activeProblem]) {
      setPlaySpeed(0);
      return;
    }
    if (activeProblem === problems.length - 1) {
      const [
        newProblem,
        newFinished,
        newAugmentedPath,
        newLevelEdges,
        newBlockingEdges,
        newDistanceLabels,
        newQ,
        newCurEdge,
        newXf,
        newGoldBergTarjanInitDone,
        newQHead,
        newQTail
      ] = (() => {
        const p = JSON.parse(JSON.stringify(problems.at(-1) as MaxFlowProblem));
        switch (algo) {
          case 'ford-fulkerson':
            return stepFordFulkerson(p, [], [], 0, [], true, 0, 0);
          case 'edmonds-karp':
            return stepEdmondsKarp(p, [], [], 0, [], true, 0, 0);
          case 'dinic':
            return stepDinic(p, [], [], 0, [], true, 0, 0);
          case 'goldberg-tarjan':
          default:
            return stepGoldbergTarjan(
              p,
              JSON.parse(JSON.stringify(distanceLabels[distanceLabels.length - 1])),
              q.current,
              curEdge.current,
              xf.current,
              goldBergTarjanInitDone.current,
              qHead.current,
              qTail.current
            );
        }
      })();
      setProblems((old) => [...old, newProblem]);
      setFinisheds((old) => [...old, newFinished]);
      if (algo === 'ford-fulkerson' || algo === 'edmonds-karp' || algo === 'goldberg-tarjan') {
        setAugmentedPaths((old) => [...old, newAugmentedPath]);
      } else if (algo === 'dinic') {
        setLevelEdges((old) => [...old, newLevelEdges]);
        setBlockingEdges((old) => [...old, newBlockingEdges]);
      }
      if (algo === 'goldberg-tarjan') {
        setDistanceLabels((old) => [...old, newDistanceLabels]);
        q.current = newQ;
        curEdge.current = newCurEdge;
        xf.current = newXf;
        goldBergTarjanInitDone.current = newGoldBergTarjanInitDone;
        qHead.current = newQHead;
        qTail.current = newQTail;
      }
    }
    setActiveProblem((old) => old + 1);
  };

  if (!showParametrization && problems.length === 0) {
    setProblems([
      generateProblemWithLocation(
        singleProblemParametrization.noNodes,
        singleProblemParametrization.maxCapacity
      )
    ]);
  }
  if (!showParametrization && problems.length > 0 && problems[activeProblem] && canvas) {
    const { width, height } = (canvas.parentElement as HTMLElement).getBoundingClientRect();
    smallDim.current = min(width, height);
    canvas.width = width;
    canvas.height = height;
    const initialCenteringOffset = [
      width > height ? (width - height) / 2 : 0,
      height > width ? (height - width) / 2 : 0
    ];
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    ctx.font = `${fontSize * zoom}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = lineWidth * zoom;

    // Function for converting world coordinates (the one generated in problem-generator) to camera coordinates (the one used for drawing to canvas)
    const toCameraSpace = (pos: [number, number]) => {
      return add(
        multiply(add(multiply(subtract(add(pos, dragOffset), 0.5), zoom), 0.5), smallDim.current),
        initialCenteringOffset
      ) as [number, number];
    };

    // Calculate saturated cut for Goldberg Tarjan
    const saturatedCut: boolean[] = [];
    if (algo === 'goldberg-tarjan') {
      const q = [problems[activeProblem].source];
      saturatedCut[problems[activeProblem].source] = true;
      let u = -1;
      while (q.length > 0) {
        u = q.shift() as number;
        for (const v of problems[activeProblem].graph.cachedTargetKeys[u]) {
          if (
            !saturatedCut[v] &&
            problems[activeProblem].graph.edges[u][v].residualCapacity !== 0
          ) {
            saturatedCut[v] = true;
            q.push(v);
          }
        }
      }
    }

    // Draw edges
    for (let u = 0; u < problems[activeProblem].graph.nodes.length; u += 1) {
      for (const v of problems[activeProblem].graph.cachedTargetKeys[u]) {
        const edge = problems[activeProblem].graph.edges[u][v];
        let sourcePos: [number, number] = problems[activeProblem].graph.nodes[u].position;
        let targetPos: [number, number] = problems[activeProblem].graph.nodes[v].position;
        const direction = subtract(targetPos, sourcePos);
        const normal = normalize([-direction[1], direction[0]]);
        sourcePos = add(sourcePos, multiply(normal, edgeSpacing)) as [number, number];
        targetPos = add(targetPos, multiply(normal, edgeSpacing)) as [number, number];
        let arrowHeadStart = subtract(
          targetPos,
          multiply(
            normalize(subtract(targetPos, sourcePos)),
            algo === 'goldberg-tarjan' ? 2 * nodeSize : nodeSize
          )
        ) as [number, number];
        let arrowHeadEnd = add(
          subtract(
            arrowHeadStart,
            multiply(normalize(subtract(targetPos, sourcePos)), arrowHeadRelativePosition[1])
          ),
          multiply(normal, arrowHeadRelativePosition[0])
        ) as [number, number];

        let edgeLabelPos = add(
          divide(add(sourcePos, targetPos), 2),
          multiply(
            normalize(
              multiply(
                [
                  [0, -1],
                  [1, 0]
                ],
                subtract(targetPos, sourcePos)
              )
            ),
            labelOffset
          )
        ) as [number, number];
        let oldEdgeLabelPos = add(
          divide(add(sourcePos, targetPos), 2),
          multiply(
            normalize(
              multiply(
                [
                  [0, -1],
                  [1, 0]
                ],
                subtract(targetPos, sourcePos)
              )
            ),
            2.3 * labelOffset
          )
        ) as [number, number];
        sourcePos = toCameraSpace(sourcePos);
        targetPos = toCameraSpace(targetPos);
        arrowHeadStart = toCameraSpace(arrowHeadStart);
        arrowHeadEnd = toCameraSpace(arrowHeadEnd);
        edgeLabelPos = toCameraSpace(edgeLabelPos);
        oldEdgeLabelPos = toCameraSpace(oldEdgeLabelPos);
        const gradient = ctx.createLinearGradient(...sourcePos, ...targetPos);
        gradient.addColorStop(0, '#8f99a8');
        gradient.addColorStop(1, '#020f24');
        ctx.strokeStyle = gradient;
        ctx.fillStyle = gradient;
        ctx.lineWidth = lineWidth * zoom;
        // Edges on augmented path and blocking flow are painted pink and bold
        let augmentedPathOrBlockingFlow;
        if (algo === 'ford-fulkerson' || algo === 'edmonds-karp' || algo === 'goldberg-tarjan') {
          for (let i = 0; i < augmentedPaths[activeProblem].length - 1; i += 1) {
            if (
              augmentedPaths[activeProblem][i] === u &&
              augmentedPaths[activeProblem][i + 1] === v
            ) {
              augmentedPathOrBlockingFlow = true;
              break;
            }
          }
        } else if (algo === 'dinic') {
          augmentedPathOrBlockingFlow =
            blockingEdges[activeProblem][u] && blockingEdges[activeProblem][u][v];
        }
        if (augmentedPathOrBlockingFlow) {
          const gradient = ctx.createLinearGradient(...sourcePos, ...targetPos);
          gradient.addColorStop(0, '#f2b6e9');
          gradient.addColorStop(1, '#ff00d9');
          ctx.strokeStyle = gradient;
          ctx.fillStyle = gradient;
          ctx.lineWidth = lineWidth * zoom * 2.5;
          ctx.fillText(
            `${problems[activeProblem - 1].graph.edges[u][v].residualCapacity}`,
            oldEdgeLabelPos[0],
            oldEdgeLabelPos[1]
          );
          ctx.fillText(
            `${problems[activeProblem - 1].graph.edges[u][v].residualCapacity}`.replace(/./g, '—'),
            oldEdgeLabelPos[0],
            oldEdgeLabelPos[1]
          );
          ctx.fillStyle = '#ff00d9';
          // Edges on level graph are painted turquoise
        } else if (algo === 'dinic') {
          if (levelEdges[activeProblem][u] && levelEdges[activeProblem][u][v]) {
            const gradient = ctx.createLinearGradient(...sourcePos, ...targetPos);
            gradient.addColorStop(0, '#A6EDF5');
            gradient.addColorStop(1, '#13AEE7');
            ctx.strokeStyle = gradient;
            ctx.fillStyle = gradient;
            ctx.lineWidth = lineWidth * zoom * 2;
          }
        }

        ctx.beginPath();
        ctx.moveTo(sourcePos[0], sourcePos[1]);
        ctx.lineTo(targetPos[0], targetPos[1]);
        ctx.moveTo(arrowHeadStart[0], arrowHeadStart[1]);
        ctx.lineTo(arrowHeadEnd[0], arrowHeadEnd[1]);
        ctx.closePath();
        ctx.stroke();
        ctx.fillText(`${edge.residualCapacity}`, edgeLabelPos[0], edgeLabelPos[1]);
      }
    }

    // Draw nodes
    problems[activeProblem].graph.nodes.forEach((node, u) => {
      // Looks kind of ugly. Wish TypeScript had operator overloading.
      const pos = toCameraSpace(node.position);
      ctx.fillStyle = 'black';
      if (u === problems[activeProblem].source) {
        const textPos = toCameraSpace(add(node.position, [0.015, -0.015]));
        ctx.fillText('s', textPos[0], textPos[1]);
      } else if (u === problems[activeProblem].target) {
        const textPos = toCameraSpace(add(node.position, [0.015, -0.015]));
        ctx.fillText('t', textPos[0], textPos[1]);
      }
      if (algo === 'goldberg-tarjan') {
        ctx.fillStyle = saturatedCut[u] ? 'blue' : 'red';
        ctx.beginPath();
        ctx.arc(pos[0], pos[1], 2 * nodeSize * smallDim.current * zoom, 0, 360);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = 'white';
        if (u === problems[activeProblem].source) ctx.fillStyle = '#0091ff';
        else if (u === problems[activeProblem].target) ctx.fillStyle = '#0091ff';
        if (
          activeProblem >= 1 &&
          distanceLabels[activeProblem - 1][u] !== distanceLabels[activeProblem][u]
        ) {
          ctx.fillStyle = '#f862e2';
        }
        ctx.beginPath();
        ctx.arc(pos[0], pos[1], 1.8 * nodeSize * smallDim.current * zoom, 0, 360);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = 'black';
        ctx.fillText(
          typeof distanceLabels[activeProblem][u] === 'undefined'
            ? '-'
            : `${distanceLabels[activeProblem][u]}`,
          pos[0],
          pos[1]
        );
      } else {
        ctx.fillStyle = '#4a515c';
        if (u === problems[activeProblem].source) ctx.fillStyle = '#0091ff';
        else if (u === problems[activeProblem].target) ctx.fillStyle = '#0091ff';
        ctx.beginPath();
        ctx.arc(
          pos[0],
          pos[1],
          (u === problems[activeProblem].source || u === problems[activeProblem].target ? 1.5 : 1) *
            nodeSize *
            smallDim.current *
            zoom,
          0,
          360
        );
        ctx.closePath();
        ctx.fill();
      }
    });
  }

  const animate = (tNow: DOMHighResTimeStamp) => {
    animationFrame.current = requestAnimationFrame(animate);
    if (tNow - tLastTick.current > playSpeeds[playSpeed] && !stepForwardLocked.current) {
      stepForwardLocked.current = true;
      tLastTick.current = tNow;
      stepForward();
      setTimeout(() => {
        stepForwardLocked.current = false;
      });
    }
  };

  useEffect(() => {
    animationFrame.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    };
  }, [animate, stepForward, playSpeed]);

  if (showParametrization) {
    return <SingleProblemParametrization done={() => setShowParametrization(false)} />;
  }

  return (
    <div className={styles.Visualization} data-testid="Visualization">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className={styles.content}
        onWheel={onWheel}
        onMouseDown={dragStart}
        onMouseMove={drag}
        onMouseLeave={dragEnd}
        onMouseUp={dragEnd}
        onBlur={dragEnd}>
        <canvas ref={canvasRef} />
      </div>
      <div className={styles.controls}>
        <div className={styles.automatic}>
          <button
            onClick={() => setPlaySpeed(0)}
            type="button"
            className={playSpeed === 0 ? styles.active : ''}>
            <FontAwesomeIcon icon={faPause} />
          </button>
          <button
            onClick={() => setPlaySpeed(1)}
            type="button"
            className={`${playSpeed === 1 ? styles.active : ''} ${
              finisheds[activeProblem] ? styles.disabled : ''
            }`}>
            <FontAwesomeIcon icon={faPlay} />
          </button>
          <button
            onClick={() => setPlaySpeed(2)}
            type="button"
            className={`${playSpeed === 2 ? styles.active : ''} ${
              finisheds[activeProblem] ? styles.disabled : ''
            }`}>
            <FontAwesomeIcon icon={faPlay} />
            <FontAwesomeIcon icon={faPlay} />
          </button>
          <button
            onClick={() => setPlaySpeed(3)}
            type="button"
            className={`${playSpeed === 3 ? styles.active : ''} ${
              finisheds[activeProblem] ? styles.disabled : ''
            }`}>
            <FontAwesomeIcon icon={faPlay} />
            <FontAwesomeIcon icon={faPlay} />
            <FontAwesomeIcon icon={faPlay} />
          </button>
          <button
            onClick={() => setPlaySpeed(4)}
            type="button"
            className={`${playSpeed === 4 ? styles.active : ''} ${
              finisheds[activeProblem] ? styles.disabled : ''
            }`}>
            <FontAwesomeIcon icon={faPlay} />
            <FontAwesomeIcon icon={faPlay} />
            <FontAwesomeIcon icon={faPlay} />
            <FontAwesomeIcon icon={faPlay} />
          </button>
        </div>
        <div className={styles.step}>
          <button
            onClick={() => !playSpeed && activeProblem !== 0 && stepBack()}
            type="button"
            className={activeProblem === 0 || playSpeed ? styles.disabled : ''}>
            <FontAwesomeIcon icon={faFastBackward} />
          </button>
          <button
            onClick={() => !playSpeed && !finisheds[activeProblem] && stepForward()}
            type="button"
            className={finisheds[activeProblem] || playSpeed ? styles.disabled : ''}>
            <FontAwesomeIcon icon={faFastForward} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Visualization;
